import { useEffect, useState } from "react";
import PaginationTable from "../pagination/Pagination4";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const CommissionSaleReportDetails = ({
  month,
  year,
  distCode,
  distMonthName,
  districtName,
}) => {
  const [verifySaleData, setVerifySalesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [submitMessage, setSubmitMessage] = useState("");
  const [showButtonData, setShowButtonData] = useState({
    buttonToShow: "",
    message: "",
  });

  useEffect(() => {
    const fetchVerifySalesDetails = async () => {
      try {
        setSubmitMessage("");
        setLoading(true);
        const salesresponse = await fetch(
          `${process.env.REACT_APP_URL}/api/commission/commission-sales?month=${month}&year=${year}&distCode=${distCode}`
        );

        if (!salesresponse.ok) {
          throw new Error("Failed to fetch Sales commission data");
        }

        const data = await salesresponse.json();
        setVerifySalesData(data);
      } catch (error) {
        console.error("Fetch Error:", error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    fetchVerifySalesDetails();
    fetchCommissionGenerationStatus();
  }, [month, year, distCode]);

  const fetchCommissionGenerationStatus = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        month,
        year,
        distCode,
        distMonthName,
        districtName
      });
      const commissionGenerationStatusResponse = await fetch(
        `${process.env.REACT_APP_URL}/api/commission/commision-generation-status?${queryParams}`
      );

      if (!commissionGenerationStatusResponse.ok) {
        throw new Error("Failed to fetch commission generation status");
      }

      const data = await commissionGenerationStatusResponse.json();
      setShowButtonData(data);
    } catch (error) {
      console.error("Fetch Error:", error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = verifySaleData.filter((item) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return Object.values(item).some(
      (value) => value && value.toString().toLowerCase().includes(query)
    );
  });

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(
      value === "all" ? verifySaleData.length : parseInt(value, 10)
    );
    setPage(0);
  };

  const totals = verifySaleData.reduce(
    (acc, item) => {
      acc.nfsaShopTotalQty += Number(item.nfsaShopTotalQty) || 0;
      acc.stateShopTotalQty += Number(item.stateShopTotalQty) || 0;
      acc.nfsaExtraQty += Number(item.nfsaExtraQty) || 0;
      acc.count += 1;
      return acc;
    },
    {
      nfsaShopTotalQty: 0,
      stateShopTotalQty: 0,
      nfsaExtraQty: 0,
      count: 0,
    }
  );

  const SubmitVerifySalesAndGenerateCommission = async (e) => {
    e.preventDefault();
    setSubmitMessage("");
    
    const payload = new URLSearchParams({
      month,
      year,
      distCode,
      distMonthName,
      districtName      
    });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_URL}/api/commission/commission-generate-verify?${payload}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        throw new Error("Error while generating commission for NFSA and State.");
      }

      const data = await response.text();
      setSubmitMessage(data);
      if (!distCode || distCode !== "0") {
        fetchCommissionGenerationStatus();
      }
    } catch (error) {
      console.error("Submit Error:", error);     
    }
  };

  const handleDownloadExcel = async (data) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sales Verification");
    const totalColumns = 5;
    const dataTime =
      data[0]?.dataTime?.substring(0, 19)?.replace("T", " ") || "--";

    // === Report Title ===
    const reportTitle = `Sales Commission Verification Report for District: ${districtName} and Distribution (Month ' Year): ${distMonthName}'${year}`;
    worksheet.mergeCells(1, 1, 1, totalColumns);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = reportTitle;
    titleCell.font = { size: 12, bold: true };
    titleCell.alignment = { horizontal: "center", vertical: "middle" };

    // === Header Row ===
    const headers = [
      "S.No",
      "Shop No.",
      "NFSA Quantity (in Kg)",
      "State Quantity (in Kg)",
      "NFSA Extra Quantity (in Kg)",
    ];
    worksheet.addRow(headers);

    // Style header row
    const headerRow = worksheet.getRow(2);
    headerRow.font = { bold: true };
    headerRow.alignment = { horizontal: "center" };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFDDDDDD" },
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // === Add Data Rows ===
    data.forEach((item, index) => {
      const row = worksheet.addRow([
        index + 1,
        item.fpsId,
        item.nfsaShopTotalQty.toFixed(3),
        item.stateShopTotalQty.toFixed(3),
        item.nfsaExtraQty.toFixed(3),
      ]);

      // Align columns
      row.getCell(1).alignment = { horizontal: "left" };
      row.getCell(2).alignment = { horizontal: "left" };
      row.getCell(3).alignment = { horizontal: "right" };
      row.getCell(4).alignment = { horizontal: "right" };
      row.getCell(5).alignment = { horizontal: "right" };
    });

    // Auto width adjustment for columns
    worksheet.columns.forEach((col, index) => {
      let maxLength = 5; // minimum width to avoid super narrow columns
      const headerText = headers[index] || "";
      if (headerText.length > maxLength) maxLength = headerText.length;

      col.eachCell({ includeEmpty: true }, (cell) => {
        let cellValue = cell.value;

        // If cell value is an object (like a Date), convert to string
        if (cellValue && typeof cellValue === "object") {
          if (cellValue.richText) {
            cellValue = cellValue.richText.map((rt) => rt.text).join("");
          } else if (cellValue.text) {
            cellValue = cellValue.text;
          } else if (cellValue.result) {
            cellValue = cellValue.result.toString();
          } else {
            cellValue = cellValue.toString();
          }
        } else if (cellValue === null || cellValue === undefined) {
          cellValue = "";
        } else {
          cellValue = cellValue.toString();
        }

        if (cellValue.length > maxLength) {
          maxLength = cellValue.length;
        }
      });

      // Optionally limit max width so columns don't get huge
      const maxAllowedWidth = 30;
      col.width = maxLength > maxAllowedWidth ? maxAllowedWidth : maxLength + 2;
    });

    // Calculate totals manually first
    const totals = verifySaleData.reduce(
      (acc, item) => {
        acc.nfsaShopTotalQty += Number(item.nfsaShopTotalQty.toFixed(3)) || 0;
        acc.stateShopTotalQty += Number(item.stateShopTotalQty.toFixed(3)) || 0;
        acc.nfsaExtraQty += Number(item.nfsaExtraQty.toFixed(3)) || 0;
        acc.count += 1;
        return acc;
      },
      {
        nfsaShopTotalQty: 0,
        stateShopTotalQty: 0,
        nfsaExtraQty: 0,
      }
    );

    // Add total values row
    const totalRow = worksheet.addRow([
      "",
      "Total",
      totals.nfsaShopTotalQty.toFixed(3),
      totals.stateShopTotalQty.toFixed(3),
      totals.nfsaExtraQty.toFixed(3),
    ]);

    // Style total row
    totalRow.font = { bold: true };
    totalRow.alignment = { horizontal: "right" };
    totalRow.getCell(1).alignment = { horizontal: "left" };

    totalRow.eachCell((cell, colNumber) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "double" },
        right: { style: "thin" },
      };
      // Keep alignment consistent for merged cells
      if (colNumber <= 2) {
        cell.alignment = { horizontal: "left" };
      }
    });

    const dataTimeRow = worksheet.addRow([`Data Time: ${dataTime}`]);

    worksheet.mergeCells(
      dataTimeRow.number,
      1,
      dataTimeRow.number,
      totalColumns
    );

    dataTimeRow.alignment = { horizontal: "right" };
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Sales_Verification.xlsx`);
  };


  if (verifySaleData.length === 0) {
    return (
      <div className="mainhead" align="center" style={{width:'80%'}}>
        No data found
      </div>
    );
  }

  return (
    <>
      {submitMessage && (
        <div
          className="mainhead"
          align="center"
          style={{
            textDecoration: "none",
            marginTop: "5px",
            marginBottom: "20px",
            width:'80%'
          }}
        >
          {submitMessage}
        </div>
      )}

      <div style={{ justifyContent: "center", marginBottom: "70px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "90%",
            margin: "0 auto 20px auto",
          }}
        >
          {<div style={{ width: "150px" }}></div>}

          <div style={{ align: "center" }}>
            <PaginationTable
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </div>

          <div>
            <input
              type="text"
              placeholder="Search "
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ padding: "8px", width: "200px" }}
            />
          </div>
        </div>

        <table width="80%" border="1" cellPadding="5" cellSpacing="0">
          <thead>
            <tr className="tableheader">
              <th colSpan={5}>
                Sales Commission Verification Report for District:{" "}
                {districtName} and Distribution (Month'Year) : {distMonthName}'
                {year}
              </th>
            </tr>
            <tr className="tableheader">
              <th>S.No</th>
              <th>Shop No.</th>
              <th>NFSA Quantity (in Kg)</th>
              <th>State Quantity (in Kg)</th>
              <th>NFSA Extra Quantity (in Kg)</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((item, index) => (
              <tr key={index}>
                <td>
                  {rowsPerPage === "all"
                    ? index + 1
                    : index + 1 + page * rowsPerPage}
                </td>
                <td>{item.fpsId}</td>
                <td style={{ textAlign: "right" }}>
                  {item.nfsaShopTotalQty.toFixed(3)}
                </td>
                <td style={{ textAlign: "right" }}>
                  {item.stateShopTotalQty.toFixed(3)}
                </td>
                <td style={{ textAlign: "right" }}>
                  {item.nfsaExtraQty.toFixed(3)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: "bold" }} className="tableheader">
              <td colSpan={2} style={{ textAlign: "center" }}>
                Total
              </td>
              <td style={{ textAlign: "right" }}>
                {totals.nfsaShopTotalQty.toFixed(3)}
              </td>
              <td style={{ textAlign: "right" }}>
                {" "}
                {totals.stateShopTotalQty.toFixed(3)}
              </td>
              <td style={{ textAlign: "right" }}>
                {totals.nfsaExtraQty.toFixed(3)}
              </td>
            </tr>
            <tr>
              <td colSpan={5} style={{ textAlign: "right" }}>
                Data Time:{" "}
                <font style={{ color: "red" }}>
                  {paginatedData[0]?.dataTime
                    ?.substring(0, 19)
                    ?.replace("T", " ") || "--"}
                </font>
              </td>
            </tr>
            <tr>
              <td align="center" colSpan={5}>
                <button
                  onClick={() => handleDownloadExcel(verifySaleData)}
                  className="button button2"
                >
                  Download Excel
                </button>
              </td>
            </tr>
          </tfoot>
        </table>

        <div style={{ marginTop: "40px", textAlign: "center", width: "80%" }}>
          {showButtonData?.buttonToShow === "verifySales" &&
          (!showButtonData?.message ||
            showButtonData?.message.trim() === "") ? (
            <form style={{ display: "inline" }}>
              <input type="hidden" name="month" value={month} />
              <input type="hidden" name="year" value={year} />
              <input type="hidden" name="dist_code" value={distCode} />
              <button
                className="button"
                style={{ marginLeft: "20px" }}
                onClick={SubmitVerifySalesAndGenerateCommission}
              >
                Verify and Generate Commission
              </button>
            </form>
          ) : (
            <div style={{ color: "red", fontWeight: "bold", fontSize: "20px" }}>
              {showButtonData?.message}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CommissionSaleReportDetails;
